<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios, { type AxiosError } from 'axios'
import VueHCaptcha from '@hcaptcha/vue3-hcaptcha'

import { useHCaptcha } from '@/composables/useHCaptcha'
import HCaptchaNotice from '@/components/HCaptchaNotice.vue'
import { storeSignupEmail } from '@/signupEmail'
import { takeStaticSignupForm } from '@/signupStaticForm'

const { hCaptchaRef, siteKey, execute: executeHCaptcha } = useHCaptcha()

const router = useRouter()
const currentRoute = useRoute()

// Capture signup attribution, stripping the prefix so both schemes map to the same account fields.
// Internal handoffs use signup_*, external campaigns use utm_*.
function captureAttribution(prefix: string) {
  return Object.fromEntries(
    Object.entries(currentRoute.query)
      .filter(([param]) => param.startsWith(prefix))
      .map(([param, value]) => [param.slice(prefix.length), value]),
  )
}

const signupParams = captureAttribution('signup_')
const initialAttribution =
  Object.keys(signupParams).length > 0
    ? signupParams
    : captureAttribution('utm_')

//...and silently remove them from the URL.
if (Object.keys(initialAttribution).length > 0) {
  router.replace({ path: currentRoute.path, query: {} })
}

interface SignupErrors {
  email?: string[]
  password?: string[]
  first_name?: string[]
  last_name?: string[]
}

const handoff = takeStaticSignupForm()
const email = ref(handoff?.email ?? '')
const password = ref(handoff?.password ?? '')
const firstName = ref(handoff?.firstName ?? '')
const lastName = ref(handoff?.lastName ?? '')

onMounted(() => {
  if (!handoff?.focusedId) return
  const input = document.getElementById(handoff.focusedId)
  if (input instanceof HTMLInputElement) {
    input.focus()
    try {
      input.setSelectionRange(input.value.length, input.value.length)
    } catch {
      // type=email inputs refuse the selection API.
    }
  }
})

const error = ref(false)
const errors = ref<SignupErrors | null>(null)
const inFlight = ref(false)

// Handle signup form submission
async function handleSignup() {
  inFlight.value = true
  error.value = false
  errors.value = null

  const { token, error: captchaError } = await executeHCaptcha()
  if (captchaError) {
    error.value = true
    inFlight.value = false
    return
  }

  try {
    await axios.post('/api/v1/signup/', {
      ...initialAttribution,
      email: email.value,
      password: password.value,
      first_name: firstName.value,
      last_name: lastName.value,
      hcaptcha_token: token,
    })
  } catch (e) {
    const err = e as AxiosError
    error.value = true
    if (err.response?.data) {
      errors.value = err.response.data
    } else {
      console.error('Something went wrong signing up...', err.response)
    }
  } finally {
    inFlight.value = false
  }

  if (!error.value) {
    // Hard navigation on purpose: a soft route swap here shifts the whole stacked
    // layout on mobile, and all of it would count toward /signup/'s field CLS.
    storeSignupEmail(email.value)
    window.location.assign('/signup/verify-email/')
  }
}
</script>

<template>
  <div class="col-md-6">
    <h1 class="my-3">Get started with Stadia Maps</h1>
    <p>
      Free tier includes standard basemaps and basic location APIs. No credit
      card required.
    </p>
    <form @submit.prevent="handleSignup" class="me-3" id="signup-form-form">
      <div class="mb-3">
        <div class="row">
          <div class="form-group col">
            <label for="first-name" class="form-label">First Name</label>
            <input
              type="text"
              class="form-control"
              id="first-name"
              v-model="firstName"
              :class="{ 'is-invalid': errors?.first_name }"
            />
            <div class="invalid-feedback">
              {{ errors?.first_name?.shift() }}
            </div>
          </div>
          <div class="form-group col">
            <label for="last-name" class="form-label">Last Name</label>
            <input
              type="text"
              class="form-control"
              id="last-name"
              v-model="lastName"
              :class="{ 'is-invalid': errors?.last_name }"
            />
            <div class="invalid-feedback">
              {{ errors?.last_name?.shift() }}
            </div>
          </div>
        </div>
      </div>
      <div class="mb-3">
        <div class="form-group">
          <label for="email" class="form-label">Email (required)</label>
          <input
            type="email"
            class="form-control"
            id="email"
            v-model="email"
            required
            :class="{ 'is-invalid': errors?.email }"
          />
          <div class="invalid-feedback">
            {{ errors?.email?.shift() }}
          </div>
        </div>
      </div>
      <div class="mb-3">
        <div class="form-group">
          <label for="password" class="form-label">Password (required)</label>
          <input
            type="password"
            class="form-control"
            id="password"
            required
            v-model="password"
            :class="{ 'is-invalid': errors?.password }"
          />
          <div class="invalid-feedback">
            {{ errors?.password?.shift() }}
          </div>
        </div>
      </div>
      <div class="mb-3">
        <vue-h-captcha ref="hCaptchaRef" size="invisible" :sitekey="siteKey" />
      </div>
      <button
        type="submit"
        class="btn btn-signup btn-block mb-3 w-100"
        :disabled="inFlight"
      >
        <span
          v-if="inFlight"
          class="spinner-border spinner-border-sm"
          role="status"
        />
        <template v-else> Create free account</template>
      </button>
      <p>
        By signing up, you acknowledge that you have read and agree to abide by
        the terms of our
        <a href="https://stadiamaps.com/terms-of-service/" target="_blank">
          service agreement
        </a>
        and our
        <a href="https://stadiamaps.com/privacy/" target="_blank"
          >privacy policy</a
        >.
      </p>
    </form>
    <hr />
    <HCaptchaNotice />
    <div class="alert-slot">
      <div v-if="error" class="alert alert-danger" role="alert">
        Something went wrong. Please try again.
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
